import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export function Pricing() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const navigate = useNavigate();

  const packages = [
    {
      name: 'Starter Website',
      price: '$197',
      description: 'A simple website and lead capture setup for businesses that need a clean starting point.',
      features: [
        'Website design (1–3 pages)',
        'Hosting & support',
        'Logo & graphic design',
        'Lead capture form',
        'QR code form',
        'Basic email follow-up automation',
        'Custom domain email',
      ],
      cta: 'Get Started',
      highlighted: false,
      buttonStyle: 'outline',
      planId: 'hatchling',
    },
    {
      name: 'Growth Automation',
      price: '$350',
      description: 'The best fit for businesses that want missed calls handled, leads followed up, and appointments booked automatically.',
      features: [
        'Website design & hosting support',
        'Voice AI assistant',
        'Conversation AI assistant',
        'Automated calendar / scheduling',
        'Customer relationship management (CRM)',
        'Search engine optimization',
      ],
      cta: 'Launch My Automation',
      highlighted: true,
      buttonStyle: 'primary',
      badge: 'Most Popular',
      planId: 'nuggets',
    },
    {
      name: 'Scale & Ads',
      price: '$697',
      description: 'For businesses ready to add ads, reporting, and a more complete growth system.',
      features: [
        'Everything in Growth Automation',
        'Facebook & Google Ads management',
        'Advanced automation workflows',
        'Reputation management system',
        'Monthly performance reporting',
        'Priority support',
        'Monthly strategy call',
        'Social media management',
      ],
      cta: 'Scale My Business',
      highlighted: false,
      buttonStyle: 'dark',
      planId: 'titan',
    },
  ];

  const faqs = [
    {
      question: 'Is there a contract?',
      answer: 'No long-term contracts. Cancel anytime.',
    },
    {
      question: 'Do you charge setup fees?',
      answer: 'Setup fees may apply depending on project scope. Our standard monthly plans have no hidden setup fees.',
    },
    {
      question: 'Can I upgrade later?',
      answer: 'Yes. You can upgrade your package anytime as your business grows.',
    },
    {
      question: 'What is the Business Launch add-on?',
      answer: 'For a one-time fee of $997, we handle all the paperwork to officially launch your business — including LLC formation, EIN (Tax ID) registration, and initial business compliance setup. It\'s ideal for new business owners who want everything handled in one place.',
    },
    {
      question: 'Do I need the Business Launch add-on with my monthly plan?',
      answer: 'It\'s completely optional. If your business is already established with an LLC and EIN, you don\'t need it. But if you\'re starting fresh, this add-on saves you hours of paperwork and government filings.',
    },
    {
      question: 'What is the Online Store Setup add-on?',
      answer: 'For a one-time fee of $997, we design and build a fully functional online store for your business — including product pages, a shopping cart, secure checkout, and payment integration. It\'s everything you need to start selling online without lifting a finger.',
    },
  ];

  return (
    <section id="pricing" className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-secondary mb-4">
            Simple Pricing. Powerful Growth.
          </h2>
          <p className="text-xl text-gray-600 font-light">
            Choose the automation engine that fits your business.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 mb-12">
          {packages.map((pkg, index) => (
            <div
              key={index}
              className={`relative bg-white rounded-2xl p-8 border transition-all duration-300 hover:-translate-y-2 cursor-pointer ${
                pkg.highlighted
                  ? 'border-primary shadow-2xl lg:scale-105 bg-gradient-to-br from-white to-primary/5'
                  : 'border-gray-200 shadow-lg hover:shadow-xl'
              }`}
            >
              {pkg.badge && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-primary to-secondary text-white px-6 py-2 rounded-full font-semibold text-sm shadow-lg whitespace-nowrap">
                  ⭐ {pkg.badge}
                </div>
              )}

              <div className="text-center mb-8">
                <h3 className="font-display text-2xl font-bold text-secondary mb-3">
                  {pkg.name}
                </h3>
                <div className="flex items-baseline justify-center gap-2 mb-4">
                  <span className="text-5xl font-bold text-primary">{pkg.price}</span>
                  <span className="text-xl text-gray-600">/month</span>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">{pkg.description}</p>
              </div>

              <div className="space-y-4 mb-8">
                {pkg.features.map((feature, idx) => {
                  const isNewFeature = pkg.name === 'Scale & Ads' && feature === 'Social media management';
                  return (
                    <div key={idx} className={`flex items-start gap-3 ${isNewFeature ? 'relative' : ''}`}>
                      <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${isNewFeature ? 'bg-primary/20' : 'bg-primary/10'}`}>
                        <i className="ri-check-line text-primary text-sm"></i>
                      </div>
                      <span className={`text-sm leading-relaxed flex items-center gap-2 flex-wrap ${isNewFeature ? 'text-secondary font-semibold' : 'text-gray-700'}`}>
                        {feature}
                        {isNewFeature && (
                          <span className="inline-flex items-center gap-1 bg-primary text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide whitespace-nowrap animate-pulse">
                            <i className="ri-sparkling-line text-[10px]"></i> New
                          </span>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>

              <button
                onClick={() => navigate(`/checkout?plan=${pkg.planId}`)}
                className={`w-full py-4 rounded-full font-semibold text-base transition-all duration-300 whitespace-nowrap ${
                  pkg.buttonStyle === 'primary'
                    ? 'bg-primary hover:bg-primary/90 text-white shadow-lg hover:shadow-xl hover:scale-105'
                    : pkg.buttonStyle === 'dark'
                    ? 'bg-secondary hover:bg-secondary/90 text-white shadow-lg hover:shadow-xl hover:scale-105'
                    : 'bg-white hover:bg-gray-50 text-secondary border-2 border-secondary hover:border-primary hover:text-primary'
                } cursor-pointer`}
              >
                {pkg.cta}
              </button>
            </div>
          ))}
        </div>

        {/* Custom Services CTA */}
        <div className="mb-20 relative overflow-hidden rounded-2xl bg-gradient-to-r from-secondary via-secondary/95 to-secondary/90 px-8 py-8 sm:px-10 flex flex-col sm:flex-row items-center gap-6">
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full -translate-y-1/2 translate-x-1/4 pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-40 h-40 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/4 pointer-events-none" />

          <div className="relative z-10 flex items-center gap-4 flex-shrink-0">
            <div className="w-14 h-14 flex items-center justify-center bg-primary/20 rounded-xl">
              <i className="ri-customer-service-2-line text-primary text-2xl"></i>
            </div>
          </div>

          <div className="relative z-10 flex-1 text-center sm:text-left">
            <h4 className="font-display text-2xl font-bold text-white mb-2">
              Need something more custom?
            </h4>
            <p className="text-white/75 text-sm sm:text-base leading-relaxed max-w-2xl">
              If your business needs a setup that does not fit neatly into one of our packages, we can build a custom solution around your goals, workflow, and budget.
            </p>
          </div>

          <div className="relative z-10 flex-shrink-0">
            <button
              onClick={() => navigate('/#contact-form')}
              className="bg-primary hover:bg-primary/90 text-white px-6 py-3 rounded-full font-semibold text-sm transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap"
            >
              Talk to Us
            </button>
          </div>
        </div>

        {/* Add-Ons */}
        <div className="mb-20">
          <div className="text-center mb-10">
            <span className="inline-block bg-primary/10 text-primary text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
              Optional Add-Ons
            </span>
            <h3 className="font-display text-3xl font-bold text-secondary">
              Supercharge Your Plan
            </h3>
            <p className="text-gray-600 mt-2 max-w-xl mx-auto">
              One-time services to set your business up for long-term success.
            </p>
          </div>

          <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-6">
            {/* Business Launch Add-On */}
            <div className="bg-gradient-to-br from-secondary to-secondary/90 rounded-2xl p-8 text-white relative overflow-hidden flex flex-col">
              <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/4 pointer-events-none" />
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-primary/20 rounded-full translate-y-1/2 -translate-x-1/4 pointer-events-none" />
              <div className="relative z-10 flex flex-col flex-1">
                <div className="flex items-start gap-4 mb-5">
                  <div className="w-12 h-12 flex items-center justify-center bg-primary/20 rounded-xl flex-shrink-0">
                    <i className="ri-building-line text-primary text-2xl"></i>
                  </div>
                  <div>
                    <h4 className="font-display text-xl font-bold">Business Launch Package</h4>
                    <div className="flex items-baseline gap-1 mt-1">
                      <span className="text-3xl font-bold text-primary">$997</span>
                      <span className="text-white/60 text-sm">one-time</span>
                    </div>
                  </div>
                </div>
                <p className="text-white/80 text-sm leading-relaxed mb-5">
                  We handle every step to legally establish your business — from filing your LLC to securing your federal Tax ID — so you're ready to operate professionally from day one.
                </p>
                <div className="grid grid-cols-2 gap-2 mb-6 flex-1">
                  {[
                    { icon: 'ri-file-text-line', text: 'LLC Formation & Filing' },
                    { icon: 'ri-government-line', text: 'EIN / Tax ID Registration' },
                    { icon: 'ri-shield-check-line', text: 'Business Compliance Setup' },
                    { icon: 'ri-mail-send-line', text: 'Registered Agent Coordination' },
                    { icon: 'ri-bank-line', text: 'Business Banking Readiness' },
                    { icon: 'ri-customer-service-line', text: 'Guided Onboarding Support' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className={`${item.icon} text-primary text-sm`}></i>
                      </div>
                      <span className="text-white/90 text-xs">{item.text}</span>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => navigate('/#contact')}
                  className="w-full bg-primary hover:bg-primary/90 text-white px-6 py-3 rounded-full font-semibold text-sm transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap"
                >
                  Add to My Plan — $997 One-Time
                </button>
              </div>
            </div>

            {/* Online Store Setup Add-On */}
            <div className="bg-gradient-to-br from-primary to-primary/80 rounded-2xl p-8 text-white relative overflow-hidden flex flex-col">
              <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/4 pointer-events-none" />
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-secondary/20 rounded-full translate-y-1/2 -translate-x-1/4 pointer-events-none" />
              <div className="relative z-10 flex flex-col flex-1">
                <div className="flex items-start gap-4 mb-5">
                  <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-xl flex-shrink-0">
                    <i className="ri-store-2-line text-white text-2xl"></i>
                  </div>
                  <div>
                    <h4 className="font-display text-xl font-bold">Online Store Setup</h4>
                    <div className="flex items-baseline gap-1 mt-1">
                      <span className="text-3xl font-bold text-white">$997</span>
                      <span className="text-white/60 text-sm">one-time</span>
                    </div>
                  </div>
                </div>
                <p className="text-white/80 text-sm leading-relaxed mb-5">
                  We design and build a fully functional online store for your business — complete with product pages, shopping cart, secure checkout, and payment integration. Start selling online without lifting a finger.
                </p>
                <div className="grid grid-cols-2 gap-2 mb-6 flex-1">
                  {[
                    { icon: 'ri-layout-line', text: 'Custom Store Design' },
                    { icon: 'ri-price-tag-3-line', text: 'Product Catalog Setup' },
                    { icon: 'ri-shopping-cart-line', text: 'Shopping Cart & Checkout' },
                    { icon: 'ri-secure-payment-line', text: 'Payment Integration' },
                    { icon: 'ri-smartphone-line', text: 'Mobile-Optimized' },
                    { icon: 'ri-seo-line', text: 'SEO-Ready Structure' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        <i className={`${item.icon} text-white/90 text-sm`}></i>
                      </div>
                      <span className="text-white/90 text-xs">{item.text}</span>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => navigate('/checkout?plan=nuggets&addon=online-store')}
                  className="w-full bg-white hover:bg-white/90 text-primary px-6 py-3 rounded-full font-semibold text-sm transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap"
                >
                  Add to My Plan — $997 One-Time
                </button>
              </div>
            </div>
          </div>
        </div>

        <div id="faqs" className="max-w-3xl mx-auto">
          <h3 className="font-display text-3xl font-bold text-secondary text-center mb-10">
            Frequently Asked Questions
          </h3>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden transition-all duration-300 hover:shadow-md"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left cursor-pointer hover:bg-gray-50 transition-colors duration-200"
                >
                  <span className="font-semibold text-secondary text-base pr-4">
                    {faq.question}
                  </span>
                  <i
                    className={`ri-arrow-down-s-line text-2xl text-primary transition-transform duration-300 flex-shrink-0 ${
                      openFaq === index ? 'rotate-180' : ''
                    }`}
                  ></i>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openFaq === index ? 'max-h-40' : 'max-h-0'
                  }`}
                >
                  <div className="px-6 pb-5 pt-2">
                    <p className="text-gray-600 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
